import React, { useState, useEffect, Suspense } from 'react';
import * as mui from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import IASideBar from './IASideBar';
import ResponsiveContainer from '../../layout/Container';
import auditService from '../../../services/AuditService';
import appService from '../../../services/ApplicationService';
import companyService from '../../../services/CompanyService';
import RiskIdentifier from './RiskMapper/RiskIdentifier';
import RiskRater from './RiskMapper/RiskRater';
import ControlMapper from './RiskMapper/ControlMapper';
import DashboardOverview from './RiskMapper/DashboardOverview';

// Icons
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import GppBadIcon from '@mui/icons-material/GppBad';
import DashboardIcon from '@mui/icons-material/Dashboard';

const RiskMappingStepper = () => {
  const { company_id, id } = useParams();
  const navigate = useNavigate();
  
  // States for app and loading
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [riskCounts, setRiskCounts] = useState({
    total: 0,
    high: 0,
    medium: 0,
    low: 0,
    unrated: 0
  });
  const [controlCounts, setControlCounts] = useState({
    total: 0,
    mapped: 0,
    unmapped: 0
  });
  const [workpaperCounts, setWorkpaperCounts] = useState({
    total: 0,
    completed: 0,
    inProgress: 0
  });

  // Active step
  const [activeStep, setActiveStep] = useState(0);
  const [riskSelected, setRiskSelected] = useState(null);
  
  // Helper function to count controls accurately
  const countControls = async (risks, appId, companyId) => {
    let totalControls = 0;
    let mappedControls = 0;
    let risksWithControls = 0; // Track risks that have at least one control
    let uniqueRisksWithControls = new Set(); // Track unique risk IDs that have controls
    
    // First, try to count mapped controls from each risk's CONTROL_DETAILS
    risks.forEach(risk => {
      if (risk.CONTROL_DETAILS && Array.isArray(risk.CONTROL_DETAILS) && risk.CONTROL_DETAILS.length > 0) {
        totalControls += risk.CONTROL_DETAILS.length;
        mappedControls += risk.CONTROL_DETAILS.length;
        uniqueRisksWithControls.add(risk.id); // Add this risk ID to our set
      }
    });
    
    // If some risks don't have CONTROL_DETAILS, try fetching mapped controls directly
    if (uniqueRisksWithControls.size < risks.length) {
      // Get the rated risks that don't have controls detected yet
      const ratedRisks = risks.filter(risk => 
        risk.RATING && 
        risk.RATING !== 'Not Set' && 
        !uniqueRisksWithControls.has(risk.id)
      );
      
      // Fetch mapped controls for each risk
      const controlPromises = ratedRisks.map(async (risk) => {
        try {
          const mappedControlsData = await auditService.fetchMappedControls(appId, risk.id, companyId);
          if (mappedControlsData && 
              Array.isArray(mappedControlsData) && 
              mappedControlsData.length > 0 && 
              mappedControlsData[0]?.CONTROL_DETAILS && 
              Array.isArray(mappedControlsData[0].CONTROL_DETAILS) &&
              mappedControlsData[0].CONTROL_DETAILS.length > 0) {
            uniqueRisksWithControls.add(risk.id); // Add this risk ID to our set
            return mappedControlsData[0].CONTROL_DETAILS.length;
          }
          return 0;
        } catch (error) {
          console.error(`Error fetching mapped controls for risk ${risk.id}:`, error);
          return 0;
        }
      });
      
      // Wait for all promises to resolve
      const additionalControlCounts = await Promise.all(controlPromises);
      const additionalMappedControls = additionalControlCounts.reduce((sum, count) => sum + count, 0);
      
      // Update our counts with the additional controls found
      totalControls += additionalMappedControls;
      mappedControls += additionalMappedControls;
    }
    
    // Calculate the number of unique risks with controls
    risksWithControls = uniqueRisksWithControls.size;
    
    // Return both the control counts and risk mitigation counts
    return {
      total: totalControls,
      mapped: mappedControls,
      unmapped: 0,
      risksWithControls: risksWithControls, // Number of unique risks that have at least one control
      risksNeedingAction: risks.length - risksWithControls // Number of risks without any controls
    };
  };
  
  const steps = [
    { 
      name: 'Overview', 
      description: 'Risk & Control Dashboard', 
      icon: <DashboardIcon />,
      component: DashboardOverview
    },
    { 
      name: 'Identify', 
      description: 'Identify relevant risks', 
      icon: <GppMaybeIcon />,
      component: RiskIdentifier
    },
    { 
      name: 'Assess', 
      description: 'Rate identified risks', 
      icon: <GppBadIcon />,
      component: RiskRater
    },
    { 
      name: 'Mitigate', 
      description: 'Map controls to risks', 
      icon: <SecurityIcon />,
      component: ControlMapper
    }
  ];

  // Load app and company data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch app data
        const appData = await appService.fetchAppsById(id);
        setApp(appData);
        
        // Fetch company data
        const companyData = await companyService.fetchCompanyById(company_id);
        setCompany(companyData);
        
        // Fetch risk data
        const riskMapping = await auditService.fetchRiskMappingbyApp(id, company_id);
        
        if (riskMapping && riskMapping.risklist_data) {
          const risks = riskMapping.risklist_data;
          
          // Count risks by rating
          const highRisks = risks.filter(r => r.RATING === 'High').length;
          const mediumRisks = risks.filter(r => r.RATING === 'Medium').length;
          const lowRisks = risks.filter(r => r.RATING === 'Low').length;
          const unratedRisks = risks.filter(r => !r.RATING || r.RATING === 'Not Set').length;
          
          setRiskCounts({
            total: risks.length,
            high: highRisks,
            medium: mediumRisks,
            low: lowRisks,
            unrated: unratedRisks
          });
          
          // Count controls using the helper function
          const controlCounts = await countControls(risks, id, company_id);
          setControlCounts(controlCounts);
          
          // We're no longer handling workpapers/testing
          setWorkpaperCounts({
            total: 0,
            completed: 0,
            inProgress: 0
          });
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, company_id]);

  // Handle step change
  const handleStepChange = (step) => {
    setActiveStep(step);
  };

  // Create a function to select a risk for assessment/mapping
  const selectRiskForAction = (risk) => {
    setRiskSelected(risk);
    
    // Only move to next step if we're in the Identify step
    if (activeStep === 1) { // Identify step
      setActiveStep(2); // Move to Assess step
    }
    // Otherwise, stay in the current step
  };

  // Find current component
  const CurrentStepComponent = steps[activeStep].component;

  // Prepare props for step components
  const stepProps = {
    app: app,
    company: company,
    companyId: company_id,
    appId: id,
    risks: riskCounts,
    controls: controlCounts,
    selectedRisk: riskSelected,
    selectRisk: selectRiskForAction,
    nextStep: () => setActiveStep(prevStep => Math.min(prevStep + 1, steps.length - 1)),
    prevStep: () => setActiveStep(prevStep => Math.max(prevStep - 1, 0)),
    goToStep: handleStepChange,
    refreshData: async () => {
      setLoading(true);
      try {
        // Fetch app data
        const appData = await appService.fetchAppsById(id);
        setApp(appData);
        
        // Fetch company data
        const companyData = await companyService.fetchCompanyById(company_id);
        setCompany(companyData);
        
        // Fetch risk data
        const riskMapping = await auditService.fetchRiskMappingbyApp(id, company_id);
        
        if (riskMapping && riskMapping.risklist_data) {
          const risks = riskMapping.risklist_data;
          
          // Count risks by rating
          const highRisks = risks.filter(r => r.RATING === 'High').length;
          const mediumRisks = risks.filter(r => r.RATING === 'Medium').length;
          const lowRisks = risks.filter(r => r.RATING === 'Low').length;
          const unratedRisks = risks.filter(r => !r.RATING || r.RATING === 'Not Set').length;
          
          setRiskCounts({
            total: risks.length,
            high: highRisks,
            medium: mediumRisks,
            low: lowRisks,
            unrated: unratedRisks
          });
          
          // Count controls using the helper function
          const controlCounts = await countControls(risks, id, company_id);
          setControlCounts(controlCounts);
          console.log("Updated control counts:", controlCounts);
          
          // We're no longer handling workpapers/testing
          setWorkpaperCounts({
            total: 0,
            completed: 0,
            inProgress: 0
          });
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const customMainContent = (
    <ResponsiveContainer>
      {loading ? (
        <mui.Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh',
            flexDirection: 'column'
          }}
        >
          <mui.CircularProgress color="primary" size={60} thickness={4} />
          <mui.Typography variant="h6" color="textSecondary" sx={{ mt: 3 }}>
            Loading application data...
          </mui.Typography>
        </mui.Box>
      ) : (
        <mui.Box sx={{ maxWidth: '1500px', margin: '0 auto', padding: '20px' }}>
          {/* Header and breadcrumbs */}
          <mui.Breadcrumbs sx={{ mb: 3 }}>
            <mui.Link color="inherit" href="/dashboard" underline="hover">
              Dashboard
            </mui.Link>
            <mui.Link 
              color="inherit" 
              href={`/Audit/RiskandControlMapping/${company_id}`} 
              underline="hover"
            >
              Risk Mapping
            </mui.Link>
            <mui.Typography color="textPrimary">
              {app?.APP_NAME || 'Application'}
            </mui.Typography>
          </mui.Breadcrumbs>

          {/* Title */}
          <mui.Typography 
            variant="h4" 
            component="h1" 
            gutterBottom 
            sx={{ 
              fontWeight: 'bold', 
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <SecurityIcon fontSize="large" sx={{ mr: 2 }} />
            Risk & Control Assessment
            <mui.Chip 
              label={app?.APP_NAME} 
              size="medium" 
              sx={{ ml: 2, fontWeight: 'medium' }} 
              color="primary" 
              variant="outlined"
            />
          </mui.Typography>

          {/* Workflow stepper */}
          <mui.Box sx={{ my: 4 }}>
            <mui.Stepper 
              activeStep={activeStep} 
              alternativeLabel
              sx={{
                '& .MuiStepLabel-root': {
                  cursor: 'pointer' 
                }
              }}
            >
              {steps.map((step, index) => (
                <mui.Step key={step.name} completed={false} onClick={() => handleStepChange(index)}>
                  <mui.StepLabel 
                    StepIconComponent={() => (
                      <mui.Avatar 
                        sx={{ 
                          bgcolor: index === activeStep ? 'primary.main' : 'grey.300',
                          width: 40,
                          height: 40,
                          '&:hover': {
                            bgcolor: index === activeStep ? 'primary.dark' : 'grey.400',
                          }
                        }}
                      >
                        {step.icon}
                      </mui.Avatar>
                    )}
                  >
                    <mui.Typography 
                      variant="subtitle1" 
                      fontWeight={index === activeStep ? 'bold' : 'normal'}
                    >
                      {step.name}
                    </mui.Typography>
                    <mui.Typography variant="caption" color="textSecondary">
                      {step.description}
                    </mui.Typography>
                  </mui.StepLabel>
                </mui.Step>
              ))}
            </mui.Stepper>
          </mui.Box>

          {/* Current step content */}
          <mui.Paper 
            elevation={2} 
            sx={{ 
              p: 3, 
              borderRadius: 2,
              minHeight: '70vh',
              backgroundColor: '#ffffff'
            }}
          >
            <Suspense fallback={<mui.CircularProgress />}>
              <CurrentStepComponent {...stepProps} />
            </Suspense>
          </mui.Paper>
        </mui.Box>
      )}
    </ResponsiveContainer>
  );

  return <IASideBar mainContent={customMainContent} />;
};

export default RiskMappingStepper; 